<template>
  <div class="portfolio-page">
    <section class="portfolio-hero">
      <img :src="heroImage" :alt="t('portfolio.heroImageAlt')" class="portfolio-hero-image" />
      <div class="portfolio-hero-overlay"></div>
      <div class="section-wrap portfolio-hero-content">
        <p class="eyebrow">{{ t("portfolio.eyebrow") }}</p>
        <h1 class="section-title">{{ t("portfolio.title") }}</h1>
        <p class="section-lead">{{ t("portfolio.lead") }}</p>
      </div>
    </section>

    <section class="portfolio-grid-wrap section-wrap">
      <div class="portfolio-grid">
        <figure
          v-for="(image, index) in images"
          :key="image.src"
          :class="tileClass(index)"
          @click="openLightbox(index)"
        >
          <img :src="image.src" :alt="image.alt" loading="lazy" />
        </figure>
      </div>
    </section>

    <div v-if="activeImage !== null" class="lightbox" @click.self="closeLightbox">
      <button class="lightbox-close" type="button" @click="closeLightbox" :aria-label="t('portfolio.closeImage')">
        ×
      </button>
      <button
        v-if="images.length > 1"
        class="lightbox-nav lightbox-nav--prev"
        type="button"
        @click.stop="prevImage"
        :aria-label="t('portfolio.previousImage')"
      >
        ‹
      </button>
      <img :src="images[activeImage].src" :alt="images[activeImage].alt" class="lightbox-image" />
      <button
        v-if="images.length > 1"
        class="lightbox-nav lightbox-nav--next"
        type="button"
        @click.stop="nextImage"
        :aria-label="t('portfolio.nextImage')"
      >
        ›
      </button>
    </div>
  </div>
</template>

<script>
import { i18nState, t } from "../i18n";

const portfolioContext = require.context("../assets/fotos/portfolio", false, /^\.\/TZS.*\.(jpe?g|png)$/i);
const portfolioImageKeys = portfolioContext
  .keys()
  .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

export default {
  name: "Portfolio",
  data() {
    return {
      activeImage: null,
    };
  },
  computed: {
    images() {
      const locale = i18nState.locale;
      void locale;
      return portfolioImageKeys.map((key) => ({
        src: portfolioContext(key),
        alt: t("portfolio.imageAlt"),
      }));
    },
    heroImage() {
      return this.images.length ? this.images[0].src : "";
    },
  },
  methods: {
    t,
    openLightbox(index) {
      this.activeImage = index;
      document.body.style.overflow = "hidden";
    },
    closeLightbox() {
      this.activeImage = null;
      document.body.style.overflow = "";
    },
    nextImage() {
      this.activeImage = (this.activeImage + 1) % this.images.length;
    },
    prevImage() {
      this.activeImage = (this.activeImage - 1 + this.images.length) % this.images.length;
    },
    tileClass(index) {
      return index % 5 === 0 || index % 5 === 3 ? "tile tile--wide" : "tile";
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "";
  },
};
</script>

<style lang="scss" scoped>
.portfolio-page {
  padding-bottom: 4rem;
}

.portfolio-hero {
  position: relative;
  min-height: 390px;
  display: grid;
  align-items: end;
  overflow: hidden;
}

.portfolio-hero-image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.portfolio-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(120deg, rgba(47, 15, 87, 0.82) 8%, rgba(15, 20, 28, 0.75) 52%);
}

.portfolio-hero-content {
  position: relative;
  z-index: 2;
  color: #ffffff;
  padding: 3rem 0;
  display: grid;
  gap: 0.65rem;
}

.eyebrow {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.76rem;
  color: rgba(255, 255, 255, 0.82);
  font-weight: 650;
}

.portfolio-hero-content .section-title {
  font-size: clamp(1.9rem, 5vw, 3.7rem);
}

.portfolio-hero-content .section-lead {
  color: rgba(255, 255, 255, 0.88);
}

.portfolio-grid-wrap {
  margin-top: 2.2rem;
}

.portfolio-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 0.9rem;
}

.tile {
  grid-column: span 4;
  margin: 0;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(91, 28, 159, 0.1);
  box-shadow: 0 12px 26px rgba(15, 20, 28, 0.09);
  background: #ffffff;
  animation: rise 520ms ease both;
  cursor: zoom-in;
}

.tile--wide {
  grid-column: span 8;
}

.tile img {
  width: 100%;
  height: 100%;
  min-height: 260px;
  max-height: 380px;
  object-fit: cover;
  transition: transform var(--ease);
}

.tile:hover img {
  transform: scale(1.03);
}

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 20, 28, 0.88);
  padding: 2rem;
}

.lightbox-image {
  max-width: min(92vw, 1500px);
  max-height: 88vh;
  object-fit: contain;
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

.lightbox-close,
.lightbox-nav {
  position: absolute;
  border: 0;
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
  border-radius: 999px;
  width: 46px;
  height: 46px;
  font-size: 1.9rem;
  cursor: pointer;
  display: grid;
  place-items: center;
}

.lightbox-close {
  top: 1rem;
  right: 1rem;
}

.lightbox-nav--prev {
  left: 1rem;
}

.lightbox-nav--next {
  right: 1rem;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 980px) {
  .tile,
  .tile--wide {
    grid-column: span 6;
  }
}

@media (max-width: 680px) {
  .tile,
  .tile--wide {
    grid-column: span 12;
  }

  .portfolio-hero {
    min-height: 330px;
  }
}
</style>
